import Button from '@/components/UI/Button';
import Spinner from '@/components/UI/Spinner';
import { type ExportApiId } from '@/components/Studies/StudyExports/ExportsList/Exports.model';
import { triggerBrowserDownload } from '@/components/Studies/StudyExports/StudyExports.utils';
import { _notNil } from '@/littledash';
import InVivoError from '@/model/InVivoError';
import { type StudyApiId } from '@/model/Study.model';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { type AxiosRequestConfig } from 'axios';
import { useEffect, useState, type FC } from 'react';
import { RiCheckLine, RiLockFill } from 'react-icons/ri';
import { useParams } from 'react-router-dom';
import styles from './ExportDownloadConfirmation.module.scss';

type Status = { type: 'pending' } | { type: 'success' } | { type: 'access-denied' } | { type: 'error' };

const NO_REDIRECT_ON_403 = { redirect: { 403: false } } as AxiosRequestConfig;

// ApiService rethrows API errors as InVivoError, which captures the upstream
// HTTP status on context.ApiStatusCode (see InVivoError.ts:31).
const isForbiddenError = (cause: unknown): boolean =>
  InVivoError.isInVivoError(cause) && cause.context.ApiStatusCode === '403';

const ExportDownloadConfirmation: FC = () => {
  const { studyApiId, exportApiId } = useParams<{ studyApiId?: StudyApiId; exportApiId: ExportApiId }>();
  const [status, setStatus] = useState<Status>({ type: 'pending' });

  const { invoke: invokeStudyDownloadUrl } = useApiHook({
    endpoint: 'GET /api/v1/studies/{studyApiId}/exports/{exportApiId}/download-url',
    options: {
      invokeOnInit: false,
      onError: { toast: false, throw: true, slug: 'export-download-url-confirm' },
      requestConfigOverrides: NO_REDIRECT_ON_403,
    },
  });

  const { invoke: invokeTeamDownloadUrl } = useApiHook({
    endpoint: 'GET /api/v1/exports/{exportApiId}/download-url',
    options: {
      invokeOnInit: false,
      onError: { toast: false, throw: true, slug: 'export-download-url-team-confirm' },
      requestConfigOverrides: NO_REDIRECT_ON_403,
    },
  });

  const triggerDownload = async () => {
    if (!_notNil(exportApiId)) {
      setStatus({ type: 'error' });
      return;
    }
    setStatus({ type: 'pending' });
    try {
      const result = _notNil(studyApiId)
        ? await invokeStudyDownloadUrl({ path: { studyApiId, exportApiId } })
        : await invokeTeamDownloadUrl({ path: { exportApiId } });
      const { url, file_name: fileName } = result.body ?? {};
      if (!_notNil(url)) {
        setStatus({ type: 'error' });
        return;
      }
      const response = await fetch(url);
      if (!response.ok) {
        setStatus({ type: 'error' });
        return;
      }
      const blob = await response.blob();
      triggerBrowserDownload(blob, fileName ?? 'export');
      setStatus({ type: 'success' });
    } catch (cause) {
      setStatus(isForbiddenError(cause) ? { type: 'access-denied' } : { type: 'error' });
    }
  };

  useEffect(() => {
    triggerDownload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="message-page" data-test-component="ExportDownloadConfirmation" data-test-element="container">
      <div className="ui-card pa3 pa4-l mv6 tc">
        {status.type === 'pending' && (
          <div data-test-element="pending-card">
            <div className={styles.statusIcon}>
              <Spinner size="large" color="blue" />
            </div>
            <h2 className="normal f3 f2-l black pb3">Preparing your download</h2>
            <p className="f5 f4-l pb3">Fetching your document. This usually takes a few seconds.</p>
          </div>
        )}
        {status.type === 'success' && (
          <div data-test-element="success-card">
            <div className={`${styles.statusIcon} ${styles.statusIconSuccess}`}>
              <RiCheckLine size={36} />
            </div>
            <h2 className="normal f3 f2-l black pb3">Your download is ready.</h2>
            <p className="f5 f4-l pb3">
              Your document has been retrieved and is ready for download. Look for it in your Downloads folder.
            </p>
          </div>
        )}
        {status.type === 'access-denied' && (
          <div data-test-element="access-denied-card">
            <div className={`${styles.statusIcon} ${styles.statusIconError}`}>
              <RiLockFill size={36} />
            </div>
            <h2 className="normal f3 f2-l black pb3">Read Access Required.</h2>
            <p className="f5 f4-l pb3">Download unavailable. You don’t have read access to this document.</p>
          </div>
        )}
        {status.type === 'error' && (
          <div data-test-element="error-card">
            <div className={`${styles.statusIcon} ${styles.statusIconError}`}>
              <RiLockFill size={36} />
            </div>
            <h2 className="normal f3 f2-l black pb3">We couldn’t start your download.</h2>
            <p className="f5 f4-l pb3">
              Try again, or contact support if the problem persists.{' '}
              <button type="button" className="link-button" onClick={triggerDownload}>
                Try again
              </button>
            </p>
          </div>
        )}
        <Button url="/">Back to dashboard</Button>
      </div>
    </div>
  );
};

export default ExportDownloadConfirmation;
